<template>
  <div>
    <confirm-dialog ref="confirmDeleteDialog"
                    confirm-button-color="deep-orange"
                    v-bind:cancel-button-label="$t('cancel')"
                    v-bind:confirm-button-label="$t('delete')"
                    v-bind:message="$t('areYouSureYouWantToDeleteThisPositionIrrevocably')"
                    v-bind:title="$t('deletePosition')"
                    v-model="confirmDeleteDialog" />

    <v-snackbar color="deep-orange"
                right
                top
                v-model="validationSnackbar">
      {{ $t('thereAreItemsThatRequireYourAttention') }}
    </v-snackbar>

    <top-header v-bind:title="$t('position')">
      <header-button icon="mdi-chevron-left" 
                     slot="left-button"
                     v-bind:label="$t('back')"
                     v-bind:to="{ name: 'inventory-management-inventory-receipt', params: { inventoryId: inventoryId, id: inventoryReceiptId } }" />
      <header-button icon="mdi-check"
                     right
                     slot="right-button"
                     v-bind:label="$t('save')"
                     v-on:click="updateInventoryPosition" />
      <v-menu slot="menu">
        <template v-slot:activator="{ on, attrs }">
          <v-btn dark
                 icon
                 v-bind="attrs"
                 v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-on:click="deleteInventoryPosition">
            <v-list-item-title class="deep-orange--text">{{ $t('delete') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </top-header>
    <v-progress-linear color="accent"
                       indeterminate
                       v-bind:active="loading" />
    <v-container v-if="inventoryPosition != null">
      <v-row>
        <v-col cols="12"
               md="6">
          <inventory-position-details-card ref="inventoryPositionDetailsCard"
                                          v-bind:inventory-position="inventoryPosition" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import HeaderButton from '@/components/common/HeaderButton.vue'
  import TopHeader from '@/components/common/TopHeader.vue'
  import InventoryPositionDetailsCard from '@/components/inventory-management/InventoryPositionDetailsCard.vue'
  import ConfirmDialog from '@/components/common/ConfirmDialog'

  export default {
    async beforeRouteUpdate(to, _, next) {
      const id = parseInt(to.params.id)
      const inventoryId = parseInt(to.params.inventoryId)
      const inventoryReceiptId = to.params.inventoryReceiptId

      await this.get(id, inventoryId, inventoryReceiptId)

      next()
    },
    components: {
      HeaderButton,
      TopHeader,
      InventoryPositionDetailsCard,
      ConfirmDialog
    },
    computed: {
      positionNumber() {
        return this.inventoryPosition?.positionNumber
      },
      ...mapGetters({
        getInventoryPosition: 'inventoryManagement/inventoryPosition'
      })
    },
    async created() {
      const id = parseInt(this.$route.params.id)
      const inventoryId = parseInt(this.$route.params.inventoryId)
      const inventoryReceiptId = this.$route.params.inventoryReceiptId

      await this.get(id, inventoryId, inventoryReceiptId)
    },
    data: () => ({
      loading: false,
      inventoryPosition: null,
      inventoryId: null,
      inventoryReceiptId: null,
      validationSnackbar: false,
      confirmDeleteDialog: false
    }),
    methods: {
      async get(id, inventoryId, inventoryReceiptId) {
        this.inventoryId = inventoryId
        this.inventoryReceiptId = inventoryReceiptId

        this.loading = true
        
        await this.loadInventoryProducts()
        await this.loadInventoryPosition({ inventoryReceiptId, positionNumber: id })

        this.loading = false

        this.inventoryPosition = Object.assign({}, this.getInventoryPosition(inventoryReceiptId, id))
      },
      async updateInventoryPosition() {
        if (!this.$refs.inventoryPositionDetailsCard.validate()) {
          this.validationSnackbar = true

          return
        }

        this.loading = true

        await this.doUpdateInventoryPosition({ inventoryPosition: this.inventoryPosition })

        this.loading = false

        this.$router.push({ name: 'inventory-management-inventory-receipt', params: { inventoryId: this.inventoryId, id: this.inventoryReceiptId } })
      },
      async deleteInventoryPosition() {
        await this.$refs.confirmDeleteDialog.open(async r => {
          if (!r) {
            return
          }

          this.loading = true

          await this.doDeleteInventoryPosition({ inventoryReceiptId: this.inventoryReceiptId, positionNumber: this.positionNumber })

          this.loading = false

          this.$router.push({ name: 'inventory-management-inventory-receipt', params: { inventoryId: this.inventoryId, id: this.inventoryReceiptId } })
        })
      },
      ...mapActions({
        doDeleteInventoryPosition: 'inventoryManagement/deleteInventoryPosition',
        loadInventoryPosition: 'inventoryManagement/loadInventoryPosition',
        loadInventoryProducts: 'inventoryManagement/loadInventoryProducts',
        doUpdateInventoryPosition: 'inventoryManagement/updateInventoryPosition'
      })
    },
    name: 'InventoryPosition'
  }
</script>

<i18n>
  {
    "de": {
      "areYouSureYouWantToDeleteThisPositionIrrevocably": "Sind Sie sicher, dass Sie diese Position unwiderruflich löschen möchten?",
      "back": "Zurück",
      "position": "Position",
      "save": "Speichern",
      "delete": "Löschen",
      "cancel": "Abbrechen",
      "deletePosition": "Position löschen",
      "thereAreItemsThatRequireYourAttention": "Es gibt Felder, die Ihre Aufmerksamkeit erfordern."
    },
    "en": {
      "areYouSureYouWantToDeleteThisPositionIrrevocably": "Are you sure you want to delete this position irrevocably?",
      "back": "Back",
      "position": "Position",
      "save": "Save",
      "delete": "Delete",
      "cancel": "Cancel",
      "deletePosition": "Delete position",
      "thereAreItemsThatRequireYourAttention": "There are items that require your attention."
    }
  }
</i18n>